import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowsClockwiseIcon,
  CalendarBlankIcon,
  ChalkboardTeacherIcon,
  ChatIcon,
  ChatCircleIcon,
  ClockIcon,
  DotIcon,
  QuestionIcon,
  SparkleIcon,
  XIcon
} from '@phosphor-icons/react';
import { usePostHog } from 'src/lib/posthog';
import { formatRelativeTime } from 'src/lib/utils/formatRelativeTime';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import useAuth from 'src/hooks/useAuth';
import { auth } from 'src/features/auth/firebase';
import { isFirstAppView } from 'src/features/home/firstAppView';
import { useOpenAction } from 'src/features/groups/components/ActionViewer';
import { prefixWithTenant, resolveTenantCode } from 'src/lib/tenantRouting';
import { useAppSelector } from 'src/app/store/hooks';
import type { RootState } from 'src/app/store/store';
import { useMobileHeaderActions } from 'src/components/layouts/MobileHeaderActionsContext';
import Page from 'src/components/Page';
import Button from '@/components/ui/Button';
import ProductNoteBanner, { productNoteBackgroundClass } from './ProductNoteBanner';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { CountBadge } from '@/components/ui/CountBadge';
import Error from '@/components/ui/Error';
import SegmentedControl from '@/components/ui/SegmentedControl';
import Skeleton from '@/components/ui/Skeleton';
import TitledPanel from '@/components/ui/TitledPanel';
import type { ChatRequest } from 'src/features/chat/hooks/useChatStream';
import { stashHomeChatRequest } from 'src/features/chat/pendingChatRequest';
import { TRAINING_CALENDAR_URL } from 'src/features/help/links';
import { apiGet, apiPost } from 'src/lib/api/axios';
import { resourcePath, RESOURCE_ICON } from 'src/features/chat/components/ResourceLink';
import { getActionDisplayStatus } from 'src/features/groups/actionStatus';
import { getActions } from 'src/features/groups/api';
import ActionsDataTable from 'src/features/groups/components/ActionsDataTable';
import GroupsOverviewMarkdown from 'src/features/groups/components/GroupsOverviewMarkdown';
import { ActivityTimeline, RequiresActionCardList } from 'src/features/groups/components/GroupHomeTab';
import { getRecentActivityIcon, useGroupsHomeActivity } from 'src/features/groups/components/GroupsActivityHighlights';
import { OVERVIEW_FOLLOW_UP_BUTTON_CLASS } from 'src/features/groups/components/GroupsOverviewAskInput';
import { useDirectoryOverview, useDirectorySnapshot } from 'src/features/groups/hooks';
import logo from 'src/assets/icons/icon.png';
import GuestMembershipPrompt from './GuestMembershipPrompt';
import HomeChatComposer from './HomeChatComposer';
import { SpecialistProvider } from 'src/contexts/SpecialistContext';
import { FreemiumQuotaStatus } from 'src/features/chat/components/FreemiumQuotaNotice';
import WorkIntentButtons from 'src/features/chat/components/WorkIntentButtons';
import { useChatQuota } from 'src/features/chat/hooks/useChatQuota';
import useChatUrlState from 'src/features/chat/hooks/useChatUrlState';
import { useIsFreemiumUser } from 'src/hooks/useIsFreemiumUser';
import { directoryCardToGroup } from 'src/features/groups/utils';
import SharedResources from './SharedResources';

type HomeProductNote = {
  id: string;
  title: string;
  body: string;
  action: 'prompt' | 'link';
  try_target: string;
  background: string;
};

function Rise({ delay, children }: { delay: number; children: ReactNode }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.45, delay: reduceMotion ? 0 : delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function ProductUpdateCard({ onTry }: { onTry: (prompt: string) => void }) {
  const posthog = usePostHog();
  const navigate = useTenantNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [index, setIndex] = useState(0);
  const indexRef = useRef(index);
  indexRef.current = index;
  const { data: updates = [] } = useQuery({
    queryKey: ['product-notes', user?.user_id ?? null],
    queryFn: async () => {
      const response = await apiGet<HomeProductNote[]>('/product-notes', {
        skipTenantPrefix: true,
        meta: { globalError: false, context: 'home-product-notes' }
      });
      return response.data;
    }
  });
  const update = updates[Math.min(index, Math.max(updates.length - 1, 0))];
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', containScroll: 'trimSnaps' });

  const recordPage = (nextIndex: number) => {
    const note = updates[nextIndex];
    if (!note || nextIndex === indexRef.current) return;
    posthog?.capture('refresh_home_product_note_paged', {
      note_id: note.id,
      title: note.title,
      index: nextIndex
    });
  };

  const showNote = (nextIndex: number) => {
    recordPage(nextIndex);
    setIndex(nextIndex);
  };

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const nextIndex = emblaApi.selectedScrollSnap();
      recordPage(nextIndex);
      setIndex(nextIndex);
    };
    emblaApi.on('select', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, posthog, updates]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, updates.length]);

  if (!update) return null;

  const tryNote = (item: HomeProductNote) => {
    posthog?.capture('refresh_home_product_note_clicked', {
      note_id: item.id,
      title: item.title,
      action: item.action
    });
    if (item.action === 'link') {
      navigate(item.try_target);
      return;
    }
    onTry(item.try_target);
  };

  const dismiss = (id: string) => {
    const note = updates.find((item) => item.id === id);
    posthog?.capture('refresh_home_product_note_dismissed', { note_id: id, title: note?.title });
    queryClient.setQueryData<HomeProductNote[]>(['product-notes', user?.user_id ?? null], (current) =>
      (current ?? []).filter((item) => item.id !== id)
    );
    setIndex((current) => Math.min(current, Math.max(updates.length - 2, 0)));
    void apiPost(`/product-notes/${id}/dismiss`, null, {
      skipTenantPrefix: true,
      meta: { globalError: false, context: 'home-product-note-dismiss' }
    }).catch(() => {
      void queryClient.invalidateQueries({ queryKey: ['product-notes'] });
    });
  };

  return (
    <>
      <div className="md:hidden">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex items-stretch">
            {updates.map((item) => (
              <div key={item.id} className="flex min-w-0 shrink-0 basis-[88%] pr-3">
                <div
                  className={`flex min-h-full min-w-0 flex-1 flex-col gap-2 rounded-xl border border-border-default px-3.5 py-2.5 ${productNoteBackgroundClass(item.background)}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="inline-flex items-center gap-1.5 pt-1 text-xs text-content-subtle">
                      <SparkleIcon size={14} weight="fill" className="text-secondary-blue" />
                      Product note
                    </p>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button variant="primary" size="sm" text="Try it" onClick={() => tryNote(item)} />
                      <button
                        type="button"
                        aria-label={`Dismiss ${item.title}`}
                        onClick={() => dismiss(item.id)}
                        className="flex size-7 shrink-0 items-center justify-center rounded-md text-content-subtle hover:bg-white/70"
                      >
                        <XIcon size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="min-w-0 text-sm font-semibold text-content-default">
                    <span>{item.title}</span>
                    <span className="text-content-subtle"> — {item.body}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
        {updates.length > 1 ? (
          <div className="mt-2 flex items-center justify-center gap-1.5">
            {updates.map((item, itemIndex) => (
              <button
                key={item.id}
                type="button"
                aria-label={`Show update ${itemIndex + 1}: ${item.title}`}
                aria-current={itemIndex === index ? 'true' : undefined}
                onClick={() => emblaApi?.scrollTo(itemIndex)}
                className={
                  itemIndex === index
                    ? 'h-1.5 w-4 rounded-full bg-secondary-blue'
                    : 'size-1.5 rounded-full bg-bluegray-300'
                }
              />
            ))}
          </div>
        ) : null}
      </div>
      <div className="hidden md:block">
        <ProductNoteBanner
          title={update.title}
          body={update.body}
          background={update.background}
          onTry={() => tryNote(update)}
          onDismiss={() => dismiss(update.id)}
          pageCount={updates.length}
          pageIndex={index}
          onPage={showNote}
        />
      </div>
    </>
  );
}

function FirstLaunchHelpBanner() {
  const navigate = useTenantNavigate();
  const posthog = usePostHog();
  const openTutorials = () => {
    posthog?.capture('refresh_home_first_launch_tutorials');
    navigate('/tutorials');
  };
  const title = 'New to CompliMind?';
  const body = 'We recommend checking out our introduction on the Help & Tutorials page.';

  return (
    <>
      <div className="md:hidden">
        <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-border-default bg-primary-lightblue px-3.5 py-2.5">
          <div className="flex items-start justify-between gap-2">
            <p className="inline-flex items-center gap-1.5 pt-1 text-xs text-content-subtle">
              <SparkleIcon size={14} weight="fill" className="text-secondary-blue" />
              Product note
            </p>
            <Button variant="primary" size="sm" text="Help & Tutorials" onClick={openTutorials} />
          </div>
          <p className="min-w-0 text-sm font-semibold text-content-default">
            <span>{title}</span>
            <span className="text-content-subtle"> — {body}</span>
          </p>
        </div>
      </div>
      <div className="hidden md:block">
        <ProductNoteBanner
          title={title}
          body={body}
          actionLabel="Help & Tutorials"
          showDismiss={false}
          onTry={openTutorials}
        />
      </div>
    </>
  );
}

type GroupsTab = 'timeline' | 'requires_action';

const SUPPORT_EMAIL_HREF = 'mailto:support@complimind.co.uk';

function HomeHelpMenu({ compact = false }: { compact?: boolean }) {
  const posthog = usePostHog();
  const navigate = useTenantNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const openTutorials = () => {
    posthog?.capture('refresh_home_open_tutorials');
    setAnchor(null);
    navigate('/tutorials');
  };

  const openOnboarding = () => {
    posthog?.capture('refresh_home_book_onboarding');
    window.open(TRAINING_CALENDAR_URL, '_blank', 'noopener,noreferrer');
    setAnchor(null);
  };

  const openHelp = () => {
    posthog?.capture('refresh_home_ask_for_help');
    window.location.href = SUPPORT_EMAIL_HREF;
    setAnchor(null);
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="md"
        icon={QuestionIcon}
        aria-label="Get help"
        aria-haspopup="menu"
        aria-expanded={anchor != null}
        onClick={(event) => {
          const next = event.currentTarget;
          if (!(next instanceof HTMLElement)) return;
          setAnchor((current) => (current ? null : next));
        }}
      >
        {compact ? null : 'Get help'}
      </Button>
      <Menu open={anchor != null} anchorEl={anchor} onClose={() => setAnchor(null)} placement="bottom-end">
        <MenuItem icon={ChalkboardTeacherIcon} onClick={openTutorials}>
          Help & Tutorials page
        </MenuItem>
        <MenuItem icon={CalendarBlankIcon} onClick={openOnboarding}>
          Drop-in sessions
        </MenuItem>
        <MenuItem icon={ChatCircleIcon} onClick={openHelp}>
          Ask for help
        </MenuItem>
      </Menu>
    </>
  );
}

function PanelSkeleton() {
  return (
    <div className="flex flex-col gap-2" aria-busy="true">
      <Skeleton className="h-5 w-[92%]" />
      <Skeleton className="h-5 w-[72%]" />
      <Skeleton className="h-5 w-[80%]" />
    </div>
  );
}

/** Open and overdue actions, matching the Actions page default filter. */
function OverviewActions() {
  const navigate = useTenantNavigate();
  const openAction = useOpenAction();
  const actionsQuery = useQuery({
    queryKey: ['actions', 'list'],
    queryFn: getActions
  });

  const actions = useMemo(() => {
    const visible = (actionsQuery.data?.actions ?? []).filter((action) => {
      const status = getActionDisplayStatus(action);
      return status === 'open' || status === 'overdue';
    });
    return visible.sort((left, right) => right.created_at.localeCompare(left.created_at)).slice(0, 10);
  }, [actionsQuery.data?.actions]);

  if (actionsQuery.isPending) return <PanelSkeleton />;
  if (actionsQuery.isError) return <Error message="Could not load your actions." size="sm" />;
  if (actions.length === 0) {
    const hasAnyActions = (actionsQuery.data?.actions.length ?? 0) > 0;
    if (hasAnyActions) {
      return <p className="text-sm leading-5 text-content-subtle">You've finished your open actions.</p>;
    }
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm leading-5 text-content-subtle">
          Actions are tasks from meetings, reviews, and group work. Ones assigned to you show up here.
        </p>
        <Button variant="outline" size="sm" text="Create your first action" onClick={() => navigate('/actions')} />
      </div>
    );
  }

  return (
    <ActionsDataTable
      data={actions}
      itemsPerPage={10}
      showAssignees={false}
      onRowClick={(action) => openAction?.(action.id)}
    />
  );
}

/** Activity domains whose events point at an openable resource. */
const RESOURCE_ACTIVITY_DOMAINS = ['chat', 'meeting', 'review', 'writing', 'dashboard'] as const;

const RESOURCE_ACTIVITY_TYPES: Record<string, string> = {
  chat: 'chat',
  meeting: 'meeting',
  review: 'review',
  writing: 'writing',
  dashboard: 'dashboard'
};

interface ActivityResourceEvent {
  id: string;
  event_type: string;
  created_at: string;
  resource_id: string | null;
  resource_title: string | null;
  group_context_name: string | null;
}

interface RecentResource {
  id: string;
  resourceType: string;
  title: string;
  groupName: string | null;
  createdAt: string;
}

function RecentResourcesPanel({
  resources,
  isPending,
  isError,
  onOpen,
  onShowAll
}: {
  resources: RecentResource[] | undefined;
  isPending: boolean;
  isError: boolean;
  onOpen: (path: string) => void;
  onShowAll: () => void;
}) {
  const isEmpty = !isPending && !isError && (!resources || resources.length === 0);
  return (
    <TitledPanel
      title="Recent activity"
      bodyClassName="max-h-none"
      footer={
        isEmpty ? undefined : <Button variant="ghost" size="sm" text="Show my recent activity" onClick={onShowAll} />
      }
    >
      {isPending ? (
        <PanelSkeleton />
      ) : isError ? (
        <Error message="Could not load recent resources." size="sm" />
      ) : !resources || resources.length === 0 ? (
        <p className="text-sm leading-5 text-content-subtle">
          Chats, documents, and meetings you work on will show up here.
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col divide-y divide-border-subtle p-0">
          {resources.slice(0, 10).map((resource) => {
            const path = resourcePath(resource.resourceType, resource.id);
            const Icon = RESOURCE_ICON[resource.resourceType] ?? RESOURCE_ICON.document;
            return (
              <li key={resource.id}>
                <button
                  type="button"
                  onClick={() => onOpen(path)}
                  className="flex w-full min-w-0 items-center gap-2 py-2 text-left hover:bg-surface-hover/10"
                >
                  <Icon size={16} weight="light" className="shrink-0 text-content-subtle" />
                  <span className="min-w-0 flex-1 truncate text-sm text-content-default">{resource.title}</span>
                  <span className="flex shrink-0 items-center gap-1 text-xs text-content-subtlest">
                    {resource.groupName ? <span className="max-w-24 truncate">{resource.groupName}</span> : null}
                    <time dateTime={resource.createdAt} className="whitespace-nowrap">
                      {formatRelativeTime(resource.createdAt)}
                    </time>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </TitledPanel>
  );
}

function recentResourcesFromActivity(events: ActivityResourceEvent[]): RecentResource[] {
  const seen = new Set<string>();
  const resources: RecentResource[] = [];
  for (const event of events) {
    if (!event.resource_id) continue;
    const domain = event.event_type.split('.')[0];
    const resourceType = RESOURCE_ACTIVITY_TYPES[domain];
    if (!resourceType || seen.has(event.resource_id)) continue;
    seen.add(event.resource_id);
    resources.push({
      id: event.resource_id,
      resourceType,
      title: event.resource_title?.trim() || 'Untitled resource',
      groupName: event.group_context_name,
      createdAt: event.created_at
    });
  }
  return resources;
}

export default function RefreshHome({ disableChat = false }: { disableChat?: boolean }) {
  const navigate = useTenantNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { user } = useAuth();
  const isFreemiumUser = useIsFreemiumUser();
  const { isQuotaConfirmedZero } = useChatQuota();
  const chatDisabled = disableChat || isQuotaConfirmedZero;
  const { specialistId, setSpecialistId } = useChatUrlState();
  const locationState = location.state as { focusChat?: boolean; firstAppView?: boolean } | null;
  // Focus the composer unless a navigation explicitly opts out.
  const focusChat = locationState?.focusChat !== false;
  const navigationFirstView = locationState?.firstAppView === true;
  // Auth can restore after Home mounts, and useState never re-runs its initializer.
  // A navigation flag is a first view immediately; otherwise wait for a user id so a
  // null id is not stored as "returning". When the id arrives later, classify again
  // so that user's seen flag is written.
  const [firstView, setFirstView] = useState<boolean | null>(() => (navigationFirstView ? true : null));
  const userId = user?.external_id;

  useEffect(() => {
    if (!userId) return;
    setFirstView(
      isFirstAppView({
        userId,
        navigationFirstView,
        creationTime: auth.currentUser?.metadata.creationTime,
        lastSignInTime: auth.currentUser?.metadata.lastSignInTime
      })
    );
  }, [navigationFirstView, userId]);
  const tenantCode = resolveTenantCode(pathname);
  const posthog = usePostHog();
  const { addMobileHeaderAction, removeMobileHeaderAction } = useMobileHeaderActions();
  const { profileData } = useAppSelector((state: RootState) => state.profile);
  const [prefill, setPrefill] = useState<{ id: number; text: string } | null>(null);
  const prefillIdRef = useRef(0);
  const [groupsTabChoice, setGroupsTab] = useState<GroupsTab | null>(null);

  useEffect(() => {
    const actionId = addMobileHeaderAction(<HomeHelpMenu compact />, 'right');
    return () => removeMobileHeaderAction(actionId);
  }, [addMobileHeaderAction, removeMobileHeaderAction]);

  const showWorkspacePanels = !isFreemiumUser;
  const directorySnapshot = useDirectorySnapshot(showWorkspacePanels);
  const memberCards = useMemo(
    () => (directorySnapshot.data?.groups ?? []).filter((card) => card.is_member),
    [directorySnapshot.data?.groups]
  );
  const memberGroups = useMemo(() => memberCards.map(directoryCardToGroup), [memberCards]);
  const snapshotByGroupId = useMemo(() => new Map(memberCards.map((card) => [card.group_id, card])), [memberCards]);
  const hasMemberGroups = memberGroups.length > 0;
  const showGroupsEmpty = !directorySnapshot.isPending && !directorySnapshot.isError && !hasMemberGroups;
  const directoryOverview = useDirectoryOverview(
    showWorkspacePanels && !directorySnapshot.isPending && hasMemberGroups
  );
  const groupsActivity = useGroupsHomeActivity(
    memberGroups,
    snapshotByGroupId,
    showWorkspacePanels && directorySnapshot.isPending
  );
  const groupsTab = groupsTabChoice ?? (groupsActivity.requiresActionItems.length > 0 ? 'requires_action' : 'timeline');
  const timelineGroups = useMemo(
    () =>
      groupsActivity.previewGroups.map((day) => ({
        ...day,
        items: day.items.map((item) => ({
          ...item,
          subtitleParts: item.groupName ? [item.groupName] : []
        }))
      })),
    [groupsActivity.previewGroups]
  );

  useEffect(() => {
    posthog?.capture('refresh_home_viewed');
  }, [posthog]);

  const openChat = useCallback(
    (query: string) => {
      navigate(`/chat?q=${encodeURIComponent(query)}`);
    },
    [navigate]
  );

  const overview = directoryOverview.data;
  useEffect(() => {
    if (!overview?.body) return;
    posthog?.capture('refresh_home_overview_shown', {
      link_count: overview.resource_links?.length ?? 0,
      suggestion_count: overview.follow_ups.length,
      status: overview.status
    });
  }, [overview?.body, overview?.generated_at, overview?.status, posthog]);

  const handleComposerSubmit = useCallback(
    (request: ChatRequest) => {
      posthog?.capture('refresh_home_chat_submitted');
      stashHomeChatRequest({
        ...request,
        parts: request.parts.map((part) =>
          part.type === 'text' ? { ...part, text: part.text.replaceAll('\u00A0', ' ') } : part
        )
      });
      const params = new URLSearchParams();
      const queryText = request.parts
        .filter((part): part is Extract<ChatRequest['parts'][number], { type: 'text' }> => part.type === 'text')
        .map((part) => part.text.replaceAll('\u00A0', ' '))
        .join('')
        .trim();
      if (queryText) {
        params.set('q', queryText);
      }
      if (request.specialist_id) {
        params.set('specialist_id', request.specialist_id);
      }
      if (request.google_search) {
        params.set('google_search', 'true');
      }
      const query = params.toString();
      navigate(query ? `/chat?${query}` : '/chat');
    },
    [navigate, posthog]
  );

  const choosePrompt = useCallback(
    (phrase: string) => {
      prefillIdRef.current += 1;
      setPrefill({ id: prefillIdRef.current, text: phrase });
      posthog?.capture('refresh_home_prompt_selected', { phrase });
    },
    [posthog]
  );

  const firstName = profileData?.firstname?.trim();
  const overviewBody = directoryOverview.data?.body ?? 'No overview yet across your groups.';
  const followUps = directoryOverview.data?.follow_ups ?? [];
  const actionsQuery = useQuery({
    queryKey: ['actions', 'list'],
    queryFn: getActions,
    enabled: showWorkspacePanels
  });
  const hasAnyActions = (actionsQuery.data?.actions.length ?? 0) > 0;
  const showActionsIntro = !actionsQuery.isPending && !actionsQuery.isError && !hasAnyActions;
  const recentResourcesQuery = useQuery({
    queryKey: ['account', 'activity', 'recent-resources', tenantCode],
    enabled: Boolean(tenantCode),
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50' });
      for (const domain of RESOURCE_ACTIVITY_DOMAINS) params.append('event_domain', domain);
      const response = await apiGet<{ events: ActivityResourceEvent[] }>(
        prefixWithTenant(`/account/activity?${params.toString()}`, tenantCode ?? ''),
        { skipTenantPrefix: true }
      );
      return recentResourcesFromActivity(response.data.events);
    }
  });
  const panelsReady =
    (isFreemiumUser ||
      (!directorySnapshot.isPending &&
        (!hasMemberGroups || directoryOverview.isFetched || directoryOverview.isError) &&
        !actionsQuery.isPending)) &&
    (!tenantCode || recentResourcesQuery.isFetched || recentResourcesQuery.isError);

  return (
    <Page title="Home | CompliMind">
      <div className="flex min-h-dvh w-full flex-col">
        <div
          className="relative mx-auto flex w-full min-w-0 max-w-[1280px] flex-col gap-8 overflow-x-clip px-4 pb-8 md:px-16"
          style={{ paddingTop: 'clamp(4rem, calc(50dvh - 18rem), 14rem)' }}
        >
          <header className="flex min-w-0 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3 md:gap-5">
              <img src={logo} alt="" className="h-10 pb-1 md:h-14 md:pb-3" />
              <h1 className="min-w-0 text-2xl font-semibold text-balance text-content-default md:text-4xl">
                {`${firstView === false ? 'Welcome back' : 'Welcome'}${firstName ? `, ${firstName}` : ''}!`}
              </h1>
            </div>
            <div className="hidden shrink-0 md:block">
              <HomeHelpMenu />
            </div>
          </header>

          <SpecialistProvider specialistId={isFreemiumUser ? null : specialistId} setSpecialistId={setSpecialistId}>
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <div className={`min-w-0 flex-1 ${isQuotaConfirmedZero ? 'pointer-events-none opacity-50' : ''}`}>
                  <WorkIntentButtons disabled={chatDisabled} onChoose={choosePrompt} />
                </div>
                <FreemiumQuotaStatus />
              </div>

              <HomeChatComposer
                onSubmit={handleComposerSubmit}
                prefill={prefill}
                autoFocus={focusChat}
                disabled={chatDisabled}
                disabledReason={
                  !disableChat && isQuotaConfirmedZero
                    ? 'Weekly message quota exceeded. It resets Monday at 12am.'
                    : undefined
                }
              />
            </div>
          </SpecialistProvider>

          {panelsReady && (
            <div className="flex flex-col gap-3">
              {(disableChat || firstView !== null) && (
                <Rise delay={0.05}>
                  {disableChat ? (
                    <GuestMembershipPrompt variant="banner" />
                  ) : firstView ? (
                    <FirstLaunchHelpBanner />
                  ) : (
                    <ProductUpdateCard onTry={choosePrompt} />
                  )}
                </Rise>
              )}

              {disableChat ? (
                <Rise delay={0.08}>
                  <TitledPanel
                    title="Shared with you"
                    subtitle="Resources other members have shared with your guest account."
                  >
                    <SharedResources variant="list" />
                  </TitledPanel>
                </Rise>
              ) : null}

              <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                {showWorkspacePanels ? (
                  <Rise delay={0.12}>
                    <div className="flex min-w-0 flex-col gap-4">
                      <TitledPanel
                        title="Your overview"
                        bodyClassName="max-h-none"
                        footer={
                          directoryOverview.data ? (
                            <div className="flex items-center justify-between gap-2 text-content-subtlest">
                              <span className="inline-flex items-center gap-1 rounded-full bg-primary-lightblue px-2 py-0.5 text-xs font-medium text-secondary-blue">
                                <SparkleIcon size={12} weight="fill" />
                                AI summary
                              </span>
                              <div className="flex items-center">
                                {directoryOverview.data.generated_at ? (
                                  <>
                                    <p className="pr-1 text-xs whitespace-nowrap">
                                      Generated {formatRelativeTime(directoryOverview.data.generated_at)}
                                    </p>
                                    <DotIcon size={14} weight="light" aria-hidden />
                                  </>
                                ) : null}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  text="Refresh"
                                  className="text-content-subtlest"
                                  icon={ArrowsClockwiseIcon}
                                  loading={directoryOverview.refresh.isPending}
                                  disabled={directoryOverview.isFetching || directoryOverview.refresh.isPending}
                                  onClick={() => directoryOverview.refresh.mutate()}
                                />
                              </div>
                            </div>
                          ) : undefined
                        }
                      >
                        {directorySnapshot.isPending ? (
                          <PanelSkeleton />
                        ) : directorySnapshot.isError ? (
                          <Error message="Could not load your overview." size="sm" />
                        ) : !hasMemberGroups ? (
                          <p className="text-sm leading-5 text-content-subtle">
                            Every week, CompliMind generates an executive summary of everything you need to know about
                            your estate. Join a group and yours will show up here.
                          </p>
                        ) : directoryOverview.isPending || (directoryOverview.isFetching && !directoryOverview.data) ? (
                          <PanelSkeleton />
                        ) : directoryOverview.isError ? (
                          <div className="flex flex-col gap-2">
                            <p className="text-sm text-misc-error">Could not generate your overview.</p>
                            <Button
                              variant="outline"
                              size="sm"
                              text="Try again"
                              onClick={() => directoryOverview.refresh.mutate()}
                            />
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3">
                            <GroupsOverviewMarkdown
                              body={overviewBody}
                              resourceLinks={directoryOverview.data?.resource_links}
                              onResourceLinkClick={(link) => {
                                posthog?.capture('refresh_home_overview_link_clicked', {
                                  resource_type: link.resource_type,
                                  resource_id: link.resource_id
                                });
                              }}
                            />
                            {followUps.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {followUps.map((followUp) => (
                                  <Button
                                    key={followUp.label}
                                    variant="outline"
                                    size="sm"
                                    text={followUp.label}
                                    icon={ChatIcon}
                                    className={OVERVIEW_FOLLOW_UP_BUTTON_CLASS}
                                    title={followUp.label}
                                    disabled={disableChat}
                                    onClick={() => {
                                      posthog?.capture('refresh_home_overview_suggestion_clicked', {
                                        label: followUp.label
                                      });
                                      openChat(followUp.prompt);
                                    }}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </TitledPanel>
                      <TitledPanel
                        title="Your actions"
                        bodyClassName="max-h-none"
                        footer={
                          showActionsIntro ? undefined : (
                            <Button
                              variant="ghost"
                              size="sm"
                              text="See all actions"
                              onClick={() => navigate('/actions')}
                            />
                          )
                        }
                      >
                        <OverviewActions />
                      </TitledPanel>
                    </div>
                  </Rise>
                ) : null}
                <Rise delay={0.2}>
                  <div className="flex min-w-0 flex-col gap-4">
                    {showWorkspacePanels ? (
                      <div className={showGroupsEmpty ? 'order-2' : undefined}>
                        <TitledPanel
                          className="flex max-h-[28rem] min-h-0 min-w-0 flex-col"
                          bodyClassName="max-h-none min-h-0 flex-1"
                          title="Your groups"
                          footer={
                            showGroupsEmpty ? undefined : (
                              <Button
                                variant="ghost"
                                size="sm"
                                text="See all groups"
                                onClick={() => navigate('/groups')}
                              />
                            )
                          }
                          actions={
                            showGroupsEmpty ? undefined : (
                              <SegmentedControl
                                size="sm"
                                ariaLabel="Groups view"
                                value={groupsTab}
                                onChange={setGroupsTab}
                                disabled={!hasMemberGroups && !directorySnapshot.isPending}
                                options={[
                                  { value: 'timeline', label: 'Timeline', icon: ClockIcon },
                                  {
                                    value: 'requires_action',
                                    label: (
                                      <span className="inline-flex items-center gap-1.5">
                                        Requires action
                                        {groupsActivity.requiresActionItems.length > 0 && (
                                          <CountBadge count={groupsActivity.requiresActionItems.length} />
                                        )}
                                      </span>
                                    )
                                  }
                                ]}
                              />
                            )
                          }
                        >
                          {directorySnapshot.isPending || groupsActivity.isLoading ? (
                            <PanelSkeleton />
                          ) : directorySnapshot.isError ? (
                            <Error message="Could not load your groups." size="sm" />
                          ) : !hasMemberGroups ? (
                            <div className="flex flex-col items-start gap-3">
                              <p className="text-sm leading-5 text-content-subtle">
                                Groups bring together the people, meetings, and actions for an area of compliance or a
                                project. Join one and its timeline shows up here.
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                text="Browse groups"
                                onClick={() => navigate('/groups')}
                              />
                            </div>
                          ) : groupsTab === 'timeline' ? (
                            groupsActivity.previewGroups.length === 0 ? (
                              <p className="text-sm text-content-subtle">No recent activity across your groups.</p>
                            ) : (
                              <ActivityTimeline
                                groups={timelineGroups}
                                onItemClick={groupsActivity.handleItemClick}
                                getActivityIcon={getRecentActivityIcon}
                              />
                            )
                          ) : groupsActivity.requiresActionItems.length === 0 ? (
                            <p className="text-sm text-content-subtle">
                              Nothing in your groups needs action right now.
                            </p>
                          ) : (
                            <RequiresActionCardList
                              items={groupsActivity.requiresActionItems}
                              getActivityIcon={getRecentActivityIcon}
                              onItemClick={groupsActivity.handleItemClick}
                              showGroupName
                            />
                          )}
                        </TitledPanel>
                      </div>
                    ) : null}
                    <div className={showWorkspacePanels && showGroupsEmpty ? 'order-1' : undefined}>
                      <RecentResourcesPanel
                        resources={recentResourcesQuery.data}
                        isPending={recentResourcesQuery.isPending}
                        isError={recentResourcesQuery.isError}
                        onOpen={(path) => navigate(path)}
                        onShowAll={() => navigate('/settings/activity')}
                      />
                    </div>
                  </div>
                </Rise>
              </div>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
